import type {
  SupabaseClient,
} from '@supabase/supabase-js';

import {
  v4 as uuidv4,
} from 'uuid';

import type {
  GraphMember,
} from '@/contexts/FamilyGraphContext';

import {
  PROVISIONAL_SUBJECT_ENDPOINT_TOKEN,
  type CanonicalTreeEndpointFactPlannerResult,
} from './CanonicalTreeEndpointFactAuthoringPlanner';

import {
  evaluateCanonicalTreeSubjectIdentityResolutionCeremony,
} from './CanonicalTreeSubjectIdentityResolutionCeremony';

export const CANONICAL_TREE_FACT_PLAN_RPC =
  'execute_canonical_tree_fact_plan_atomic' as const;

export interface CanonicalTreeFactPlanRuntimeCreateSubjectProfileV2 {
  readonly name: string;

  readonly relationship:
    GraphMember['relationship'];

  readonly side:
    GraphMember['side'];

  readonly photoUrl:
    string | null;

  readonly birthYear:
    number | null;

  readonly birthDate:
    string | null;

  readonly birthMonth:
    number | null;

  readonly birthDay:
    number | null;

  readonly livingStatus:
    GraphMember['livingStatus'];

  readonly notes:
    string | null;

  readonly city:
    string | null;

  readonly description:
    string | null;

  readonly approvalStatus:
    'pending' | 'approved';

  readonly relationshipOverride:
    string | null;

  readonly relationshipWorkflow:
    GraphMember['relationshipWorkflow'];
}

export interface CanonicalTreeFactPlanRuntimeInput {
  readonly supabase: SupabaseClient;
  readonly members: readonly GraphMember[];
  readonly viewerMemberId: string | null;

  readonly plannerResult:
    CanonicalTreeEndpointFactPlannerResult;

  /*
   * Explicit C decision inputs only.
   *
   * A precomputed C result is not executable authority.
   * Stage 4 evaluates C internally against the exact
   * members, viewer, and B plan being executed.
   */
  readonly selectedExistingMemberId?:
    string | null;

  readonly createNewExplicit?:
    boolean;

  readonly similarityWarningMemberIds?:
    readonly string[];

  readonly operationId: string;

  /*
   * Historical Stage-4 v1 create input.
   * Preserved for backward compatibility.
   */
  readonly createNewSubjectName?:
    string | null;

  /*
   * Stage-5A v2 Add Relative profile contract.
   *
   * Topology, ownership, actor identity, claim state, lifecycle state
   * and branch owner are intentionally absent.
   */
  readonly createNewSubjectProfileV2?:
    CanonicalTreeFactPlanRuntimeCreateSubjectProfileV2 |
    null;

  /*
   * Future application convergence can require the server's complete
   * durable subject certificate for REUSE_EXISTING as well.
   */
  readonly requireFullSubjectMember?:
    boolean;
}

export interface CanonicalTreeFactPlanRuntimeSuccess {
  readonly ok: true;

  readonly status:
    | 'applied'
    | 'already_applied';

  readonly operationId: string;
  readonly subjectMemberId: string;
  readonly auditId: string;

  readonly createdSubject:
    boolean | null;

  /*
   * Certified GraphMember-compatible subject snapshot.
   *
   * Null remains allowed for historical Stage-4/v1 replay.
   * Stage-5A v2 and callers setting requireFullSubjectMember=true
   * require a valid value.
   */
  readonly subjectMember:
    GraphMember | null;

  readonly postState: unknown;

  readonly rpcCount: 1;
}

export interface CanonicalTreeFactPlanRuntimeFailure {
  readonly ok: false;

  readonly status:
    | 'blocked'
    | 'rpc-error'
    | 'invalid-certificate';

  readonly code: string;
  readonly message: string;

  readonly rpcCount:
    0 | 1;
}

export type CanonicalTreeFactPlanRuntimeResult =
  | CanonicalTreeFactPlanRuntimeSuccess
  | CanonicalTreeFactPlanRuntimeFailure;

export interface CanonicalTreeFactPlanPreparedRpcArgs {
  readonly p_operation_id: string;
  readonly p_viewer_member_id: string;
  readonly p_identity_mode:
    | 'REUSE_EXISTING'
    | 'CREATE_NEW_EXPLICIT';
  readonly p_canonical_subject_member_id:
    string | null;
  readonly p_create_new_subject:
    unknown;
  readonly p_fact_proposals:
    CanonicalTreeEndpointFactPlannerResult[
      'factProposals'
    ];
  readonly p_expected_state:
    readonly Record<string, unknown>[];
  readonly p_expected_authority_snapshot:
    readonly Record<string, unknown>[] |
    null;
}

export interface CanonicalTreeFactPlanRuntimeEnvelopeSuccess {
  readonly ok: true;

  readonly identityMode:
    | 'REUSE_EXISTING'
    | 'CREATE_NEW_EXPLICIT';

  readonly createNewSubjectVersion:
    | 1
    | 2
    | null;

  readonly rpcArgs:
    CanonicalTreeFactPlanPreparedRpcArgs;
}

export type CanonicalTreeFactPlanRuntimeEnvelopeResult =
  | CanonicalTreeFactPlanRuntimeEnvelopeSuccess
  | CanonicalTreeFactPlanRuntimeFailure;

const UUID_V4 =
  /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

const compareIds = (
  left: string,
  right: string,
): number =>
  left < right
    ? -1
    : left > right
      ? 1
      : 0;

const sortedUnique = (
  values: readonly string[],
): string[] =>
  Array.from(
    new Set(
      values.filter(Boolean),
    ),
  ).sort(compareIds);

const normalizedParentIds = (
  member: GraphMember,
): string[] =>
  sortedUnique(
    member.parentIds ?? [],
  );

const isRecord = (
  value: unknown,
): value is Record<string, unknown> =>
  Boolean(value) &&
  typeof value === 'object' &&
  !Array.isArray(value);

const nullableString = (
  value: unknown,
): boolean =>
  value === null ||
  typeof value === 'string';

const nullableNumber = (
  value: unknown,
): boolean =>
  value === null ||
  (
    typeof value === 'number' &&
    Number.isFinite(value)
  );

const normalizeJsonForComparison = (
  value: unknown,
): unknown => {
  if (
    value === null ||
    typeof value !== 'object'
  ) {
    return value;
  }

  if (Array.isArray(value)) {
    return value.map(
      normalizeJsonForComparison,
    );
  }

  return Object.fromEntries(
    Object.entries(
      value as Record<string, unknown>,
    )
      .sort(
        ([left], [right]) =>
          left.localeCompare(right),
      )
      .map(
        ([key, child]) => [
          key,
          normalizeJsonForComparison(
            child,
          ),
        ],
      ),
  );
};

const sameJson = (
  left: unknown,
  right: unknown,
): boolean =>
  JSON.stringify(
    normalizeJsonForComparison(left),
  ) ===
  JSON.stringify(
    normalizeJsonForComparison(right),
  );

const parseCertifiedSubjectMember = (
  value: unknown,
  expectedId: string,
): GraphMember | null => {
  if (!isRecord(value)) {
    return null;
  }

  if (
    value.id !== expectedId ||
    typeof value.ownerUserId !== 'string' ||
    typeof value.name !== 'string' ||
    value.name.trim().length === 0 ||
    typeof value.relationship !== 'string' ||
    typeof value.side !== 'string' ||
    typeof value.approvalStatus !== 'string' ||
    ![
      'pending',
      'approved',
    ].includes(
      value.approvalStatus,
    ) ||
    typeof value.confirmationsCount !==
      'number' ||
    !Number.isFinite(
      value.confirmationsCount,
    ) ||
    typeof value.createdAt !== 'string' ||
    typeof value.profileState !== 'string' ||
    ![
      'placeholder',
      'claimed',
    ].includes(
      value.profileState,
    ) ||
    !Array.isArray(
      value.parentIds,
    ) ||
    value.parentIds.some(
      parentId =>
        typeof parentId !== 'string',
    )
  ) {
    return null;
  }

  if (
    !nullableString(value.nickname) ||
    !nullableString(value.photoUrl) ||
    !nullableNumber(value.birthYear) ||
    !nullableString(value.birthDate) ||
    !nullableNumber(value.birthMonth) ||
    !nullableNumber(value.birthDay) ||
    !nullableString(value.livingStatus) ||
    !nullableString(value.notes) ||
    !nullableString(value.city) ||
    !nullableString(value.description) ||
    !nullableString(value.createdBy) ||
    !nullableString(value.spouseId) ||
    !nullableString(value.claimedByUserId) ||
    !nullableString(value.claimedAt) ||
    !nullableString(value.authEmail) ||
    !nullableString(
      value.relationshipOverride,
    ) ||
    !nullableString(
      value.branchOwnerMemberId,
    )
  ) {
    return null;
  }

  if (
    value.treeNamePreference !==
      'real-name' &&
    value.treeNamePreference !==
      'nickname'
  ) {
    return null;
  }

  if (
    value.birthdayVisibility !==
      'public' &&
    value.birthdayVisibility !==
      'private' &&
    value.birthdayVisibility !==
      'year_only'
  ) {
    return null;
  }

  if (
    typeof value.birthdayReminders !==
      'boolean'
  ) {
    return null;
  }

  if (
    value.relationshipWorkflow !== null &&
    !isRecord(
      value.relationshipWorkflow,
    )
  ) {
    return null;
  }

  return {
    ...(value as unknown as GraphMember),

    parentIds:
      [...value.parentIds] as string[],
  };
};

const certifiedSubjectMatchesCreateProfileV2 = (
  member: GraphMember,
  profile:
    CanonicalTreeFactPlanRuntimeCreateSubjectProfileV2,
): boolean =>
  member.name ===
    profile.name.trim() &&
  member.relationship ===
    profile.relationship &&
  member.side ===
    profile.side &&
  (member.photoUrl ?? null) ===
    profile.photoUrl &&
  (member.birthYear ?? null) ===
    profile.birthYear &&
  (member.birthDate ?? null) ===
    profile.birthDate &&
  (member.birthMonth ?? null) ===
    profile.birthMonth &&
  (member.birthDay ?? null) ===
    profile.birthDay &&
  (member.livingStatus ?? null) ===
    (profile.livingStatus ?? null) &&
  (member.notes ?? null) ===
    profile.notes &&
  (member.city ?? null) ===
    profile.city &&
  (member.description ?? null) ===
    profile.description &&
  member.approvalStatus ===
    profile.approvalStatus &&
  (member.relationshipOverride ?? null) ===
    profile.relationshipOverride &&
  sameJson(
    member.relationshipWorkflow ?? null,
    profile.relationshipWorkflow ?? null,
  );

export const createCanonicalTreeFactPlanOperationId =
  (): string =>
    uuidv4();

const blocked = (
  code: string,
  message: string,
): CanonicalTreeFactPlanRuntimeFailure => ({
  ok: false,
  status: 'blocked',
  code,
  message,
  rpcCount: 0,
});

const rpcFailure = (
  code: string,
  message: string,
): CanonicalTreeFactPlanRuntimeFailure => ({
  ok: false,
  status: 'rpc-error',
  code,
  message,
  rpcCount: 1,
});

const invalidCertificate = (
  code: string,
  message: string,
): CanonicalTreeFactPlanRuntimeFailure => ({
  ok: false,
  status: 'invalid-certificate',
  code,
  message,
  rpcCount: 1,
});

const planContainsProvisionalSubject = (
  plan:
    CanonicalTreeEndpointFactPlannerResult,
): boolean =>
  plan.factProposals.some(
    fact =>
      (
        fact.source.kind ===
          'provisional-subject' &&
        fact.source.token ===
          PROVISIONAL_SUBJECT_ENDPOINT_TOKEN
      ) ||
      (
        fact.target.kind ===
          'provisional-subject' &&
        fact.target.token ===
          PROVISIONAL_SUBJECT_ENDPOINT_TOKEN
      ),
  );

type CanonicalTreeRuntimeFactProposal =
  CanonicalTreeEndpointFactPlannerResult[
    'factProposals'
  ][number];

const runtimeFactEndpointKey = (
  endpoint:
    CanonicalTreeRuntimeFactProposal[
      'source'
    ],
): string =>
  endpoint.kind ===
    'canonical-member'
    ? `C:${endpoint.memberId}`
    : `P:${endpoint.token}`;

const runtimeFactSortKey = (
  fact:
    CanonicalTreeRuntimeFactProposal,
): string =>
  JSON.stringify([
    fact.factClass,
    fact.direction,
    runtimeFactEndpointKey(
      fact.source,
    ),
    runtimeFactEndpointKey(
      fact.target,
    ),
    fact.partnershipStatus,
    [...fact.evidenceMemberIds]
      .sort(compareIds),
  ]);

const resolveReuseExistingFactProposals = (
  plan:
    CanonicalTreeEndpointFactPlannerResult,
  canonicalSubjectMemberId:
    string,
):
  CanonicalTreeEndpointFactPlannerResult[
    'factProposals'
  ] => {
  const resolveEndpoint = (
    endpoint:
      CanonicalTreeRuntimeFactProposal[
        'source'
      ],
  ):
    CanonicalTreeRuntimeFactProposal[
      'source'
    ] =>
    endpoint.kind ===
      'provisional-subject' &&
    endpoint.token ===
      PROVISIONAL_SUBJECT_ENDPOINT_TOKEN
      ? Object.freeze({
          kind:
            'canonical-member' as const,
          memberId:
            canonicalSubjectMemberId,
        })
      : endpoint;

  return Object.freeze(
    plan.factProposals
      .map(
        fact =>
          Object.freeze({
            ...fact,
            source:
              resolveEndpoint(
                fact.source,
              ),
            target:
              resolveEndpoint(
                fact.target,
              ),
          }),
      )
      .sort(
        (left, right) =>
          runtimeFactSortKey(
            left,
          ).localeCompare(
            runtimeFactSortKey(
              right,
            ),
          ),
      ),
  );
};

const expectedStateFor = (
  member: GraphMember,
): Record<string, unknown> => ({
  id:
    member.id,

  ownerUserId:
    member.ownerUserId,

  claimedByUserId:
    member.claimedByUserId ??
    null,

  createdBy:
    member.createdBy ??
    null,

  profileState:
    member.profileState ??
    'placeholder',

  approvalStatus:
    member.approvalStatus ??
    null,

  /*
   * Effective graph hydration contains only
   * deleted_at IS NULL rows.
   */
  deletedAt:
    null,

  parentIds:
    normalizedParentIds(
      member,
    ),

  spouseId:
    member.spouseId ??
    null,

  relationshipWorkflow:
    member.relationshipWorkflow ??
    null,

  relationshipOverride:
    member.relationshipOverride ??
    null,
});

const authoritySnapshotFor = (
  member: GraphMember,
): Record<string, unknown> => ({
  id:
    member.id,

  ownerUserId:
    member.ownerUserId,

  parentIds:
    normalizedParentIds(
      member,
    ),

  relationship:
    member.relationship ??
    null,

  side:
    member.side ??
    null,

  approvalStatus:
    member.approvalStatus ??
    null,

  profileState:
    member.profileState ??
    'placeholder',
});

export function
prepareCanonicalTreeFactPlanRuntimeEnvelope(
  input:
    CanonicalTreeFactPlanRuntimeInput,
): CanonicalTreeFactPlanRuntimeEnvelopeResult {
  if (
    !UUID_V4.test(
      input.operationId,
    )
  ) {
    return blocked(
      'OPERATION_ID_UUID_V4_REQUIRED',
      'Canonical Tree operation id must be a caller-preserved UUID v4.',
    );
  }

  const viewerMemberId =
    input.viewerMemberId;

  if (!viewerMemberId) {
    return blocked(
      'MISSING_CANONICAL_VIEWER',
      'Canonical viewer member id is required.',
    );
  }

  const memberById =
    new Map<
      string,
      GraphMember
    >();

  for (
    const member
    of input.members
  ) {
    if (
      memberById.has(
        member.id,
      )
    ) {
      return blocked(
        'DUPLICATE_CANONICAL_MEMBER_ID',
        'Loaded canonical member snapshot contains a duplicate member id.',
      );
    }

    memberById.set(
      member.id,
      member,
    );
  }

  const viewer =
    memberById.get(
      viewerMemberId,
    ) ??
    null;

  if (!viewer) {
    return blocked(
      'STALE_CANONICAL_VIEWER',
      'Canonical viewer is absent from the loaded graph snapshot.',
    );
  }

  const plan =
    input.plannerResult;

  if (
    plan.status !==
      'READY_FOR_IDENTITY_RESOLUTION' ||
    plan.subjectIdentityResolutionRequired !==
      true ||
    plan.identityResolutionPerformed !==
      false ||
    plan.factProposals.length ===
      0 ||
    !planContainsProvisionalSubject(
      plan,
    )
  ) {
    return blocked(
      'B_PLAN_NOT_EXECUTABLE_BY_STAGE4',
      'Stage 4 requires an exact ready B plan with provisional SUBJECT identity.',
    );
  }

  /*
   * Bind C to the exact B plan entering this transaction.
   *
   * No externally computed C result can be substituted
   * because the immutable C ceremony is evaluated here
   * from this exact plannerResult and canonical snapshot.
   */
  const identity =
    evaluateCanonicalTreeSubjectIdentityResolutionCeremony({
      members:
        input.members,

      viewerMemberId,

      plannerResult:
        plan,

      selectedExistingMemberId:
        input.selectedExistingMemberId ??
        null,

      createNewExplicit:
        input.createNewExplicit ??
        false,

      similarityWarningMemberIds:
        input.similarityWarningMemberIds ??
        [],
    });

  if (
    identity.decisionExplicit !==
      true ||
    identity.ceremonyComplete !==
      true
  ) {
    return blocked(
      'C_IDENTITY_CEREMONY_INCOMPLETE',
      'Canonical subject identity ceremony must be explicitly complete.',
    );
  }

  let identityMode:
    | 'REUSE_EXISTING'
    | 'CREATE_NEW_EXPLICIT';

  let canonicalSubjectMemberId:
    string | null;

  let createNewSubject:
    | Readonly<{
        version: 1;
        name: string;
      }>
    | Readonly<{
        version: 2;
        name: string;
        relationship:
          GraphMember['relationship'];
        side:
          GraphMember['side'];
        photoUrl: string | null;
        birthYear: number | null;
        birthDate: string | null;
        birthMonth: number | null;
        birthDay: number | null;
        livingStatus:
          GraphMember['livingStatus'];
        notes: string | null;
        city: string | null;
        description: string | null;
        approvalStatus:
          'pending' | 'approved';
        relationshipOverride:
          string | null;
        relationshipWorkflow:
          GraphMember['relationshipWorkflow'];
      }>
    | null;

  if (
    identity.status ===
      'REUSE_EXISTING' &&
    identity.subjectResolution?.kind ===
      'reuse-existing'
  ) {
    identityMode =
      'REUSE_EXISTING';

    canonicalSubjectMemberId =
      identity.subjectResolution
        .memberId;

    createNewSubject =
      null;
  } else if (
    identity.status ===
      'CREATE_NEW_EXPLICIT' &&
    identity.subjectResolution?.kind ===
      'create-new-intent' &&
    identity.subjectResolution.token ===
      PROVISIONAL_SUBJECT_ENDPOINT_TOKEN
  ) {
    const legacyName =
      (
        input.createNewSubjectName ??
        ''
      ).trim();

    const profileV2 =
      input.createNewSubjectProfileV2 ??
      null;

    const profileName =
      (
        profileV2?.name ??
        ''
      ).trim();

    const name =
      profileV2
        ? profileName
        : legacyName;

    if (!name) {
      return blocked(
        'CREATE_NEW_SUBJECT_NAME_REQUIRED',
        'Explicit create-new identity requires a non-empty canonical name.',
      );
    }

    if (
      profileV2 &&
      legacyName &&
      legacyName !== profileName
    ) {
      return blocked(
        'CREATE_NEW_SUBJECT_NAME_CONFLICT',
        'Legacy and v2 canonical create names must match exactly when both are supplied.',
      );
    }

    identityMode =
      'CREATE_NEW_EXPLICIT';

    canonicalSubjectMemberId =
      null;

    if (profileV2) {
      createNewSubject =
        Object.freeze({
          version:
            2 as const,

          name,

          relationship:
            profileV2.relationship,

          side:
            profileV2.side,

          photoUrl:
            profileV2.photoUrl ??
            null,

          birthYear:
            profileV2.birthYear ??
            null,

          birthDate:
            profileV2.birthDate ??
            null,

          birthMonth:
            profileV2.birthMonth ??
            null,

          birthDay:
            profileV2.birthDay ??
            null,

          livingStatus:
            profileV2.livingStatus ??
            null,

          notes:
            profileV2.notes ??
            null,

          city:
            profileV2.city ??
            null,

          description:
            profileV2.description ??
            null,

          approvalStatus:
            profileV2.approvalStatus,

          relationshipOverride:
            profileV2.relationshipOverride ??
            null,

          relationshipWorkflow:
            profileV2.relationshipWorkflow ??
            null,
        });
    } else {
      createNewSubject =
        Object.freeze({
          version:
            1 as const,

          name,
        });
    }
  } else {
    return blocked(
      'C_IDENTITY_RESULT_INVALID',
      'C identity result does not authorize reuse-existing or explicit create-new execution.',
    );
  }

  // 003G3WF4_PART1C_END

  const executableFactProposals =
    identityMode ===
      'REUSE_EXISTING' &&
    canonicalSubjectMemberId
      ? resolveReuseExistingFactProposals(
          plan,
          canonicalSubjectMemberId,
        )
      : plan.factProposals;

  const participatingIds =
    new Set<string>([
      viewerMemberId,
    ]);

  if (
    canonicalSubjectMemberId
  ) {
    participatingIds.add(
      canonicalSubjectMemberId,
    );
  }

  for (
    const fact
    of executableFactProposals
  ) {
    if (
      fact.source.kind ===
        'canonical-member'
    ) {
      participatingIds.add(
        fact.source.memberId,
      );
    }

    if (
      fact.target.kind ===
        'canonical-member'
    ) {
      participatingIds.add(
        fact.target.memberId,
      );
    }

    for (
      const evidenceMemberId
      of fact.evidenceMemberIds
    ) {
      participatingIds.add(
        evidenceMemberId,
      );
    }
  }

  const expectedState:
    Record<string, unknown>[] =
      [];

  for (
    const memberId
    of [...participatingIds]
      .sort(compareIds)
  ) {
    const member =
      memberById.get(
        memberId,
      ) ??
      null;

    if (!member) {
      return blocked(
        'PARTICIPATING_ENDPOINT_MISSING',
        `Participating canonical endpoint is absent: ${memberId}`,
      );
    }

    if (
      member.ownerUserId !==
        viewer.ownerUserId
    ) {
      return blocked(
        'CROSS_GRAPH_PARTICIPATING_ENDPOINT',
        'All participating canonical endpoints must share the viewer graph.',
      );
    }

    expectedState.push(
      expectedStateFor(
        member,
      ),
    );
  }

  const createsAncestry =
    executableFactProposals.some(
      fact =>
        fact.createsAncestry ===
        true,
    );

  const authoritySnapshot =
    createsAncestry
      ? input.members
          .filter(
            member =>
              member.ownerUserId ===
              viewer.ownerUserId,
          )
          .map(
            authoritySnapshotFor,
          )
          .sort(
            (
              left,
              right,
            ) =>
              compareIds(
                String(left.id),
                String(right.id),
              ),
          )
      : null;

  // 003G3WF4_PART1D_END

  return Object.freeze({
    ok:
      true as const,

    identityMode,

    createNewSubjectVersion:
      createNewSubject?.version ??
      null,

    rpcArgs:
      Object.freeze({
        p_operation_id:
          input.operationId,

        p_viewer_member_id:
          viewerMemberId,

        p_identity_mode:
          identityMode,

        p_canonical_subject_member_id:
          canonicalSubjectMemberId,

        p_create_new_subject:
          createNewSubject,

        /*
         * B remains immutable planning evidence.
         * REUSE_EXISTING derives C-resolved canonical
         * execution endpoints and canonical ordering.
         * CREATE_NEW_EXPLICIT preserves exact B facts.
         */
        p_fact_proposals:
          executableFactProposals,

        p_expected_state:
          expectedState,

        p_expected_authority_snapshot:
          authoritySnapshot,
      }),
  });
}

export async function
executeCanonicalTreeFactPlanRuntime(
  input:
    CanonicalTreeFactPlanRuntimeInput,
): Promise<
  CanonicalTreeFactPlanRuntimeResult
> {
  const prepared =
    prepareCanonicalTreeFactPlanRuntimeEnvelope(
      input,
    );

  if (prepared.ok === false) {
    return prepared;
  }

  try {
    const {
      data,
      error,
    } =
      await input.supabase.rpc(
        'execute_canonical_tree_fact_plan_atomic',
        prepared.rpcArgs,
      );

    if (error) {
      return rpcFailure(
        'E_RPC_REJECTED',
        error.message ||
          'Canonical Tree atomic fact-plan RPC rejected the operation.',
      );
    }

    if (
      !data ||
      typeof data !==
        'object' ||
      Array.isArray(
        data,
      )
    ) {
      return invalidCertificate(
        'E_CERTIFICATE_OBJECT_REQUIRED',
        'Canonical Tree atomic writer returned no durable result object.',
      );
    }

    const payload =
      data as Record<
        string,
        unknown
      >;

    const status =
      payload.status;

    if (
      payload.ok !== true ||
      (
        status !==
          'applied' &&
        status !==
          'already_applied'
      )
    ) {
      return invalidCertificate(
        'E_CERTIFICATE_STATUS_INVALID',
        'Canonical Tree atomic writer returned a non-success certificate.',
      );
    }

    if (
      payload.operationId !==
      input.operationId
    ) {
      return invalidCertificate(
        'E_OPERATION_ID_MISMATCH',
        'Canonical Tree durable result operation id does not match the caller operation id.',
      );
    }

    if (
      payload.auditId !==
      input.operationId
    ) {
      return invalidCertificate(
        'E_AUDIT_ID_MISMATCH',
        'Canonical Tree durable audit id does not match the operation id.',
      );
    }

    const subjectMemberId =
      typeof payload
        .subjectMemberId ===
        'string'
        ? payload
            .subjectMemberId
        : '';

    if (!subjectMemberId) {
      return invalidCertificate(
        'E_SUBJECT_MEMBER_ID_REQUIRED',
        'Canonical Tree durable result is missing its canonical subject member id.',
      );
    }

    // 003G3WF4_PART1EA_END

    const fullSubjectRequired =
      input.requireFullSubjectMember ===
        true ||
      prepared.createNewSubjectVersion ===
        2;

    const rawSubjectMember =
      Object.prototype
        .hasOwnProperty.call(
          payload,
          'subjectMember',
        )
        ? payload.subjectMember
        : null;

    let subjectMember:
      GraphMember | null =
        null;

    if (rawSubjectMember !== null) {
      subjectMember =
        parseCertifiedSubjectMember(
          rawSubjectMember,
          subjectMemberId,
        );

      if (!subjectMember) {
        return invalidCertificate(
          'E_SUBJECT_MEMBER_CERTIFICATE_INVALID',
          'Canonical Tree atomic writer returned an invalid full subject certificate.',
        );
      }
    }

    if (
      fullSubjectRequired &&
      !subjectMember
    ) {
      return invalidCertificate(
        'E_FULL_SUBJECT_MEMBER_REQUIRED',
        'This canonical Tree operation requires a complete durable subject member certificate.',
      );
    }

    if (
      prepared.createNewSubjectVersion ===
        2
    ) {
      if (
        payload.createdSubject !==
          true
      ) {
        return invalidCertificate(
          'E_V2_CREATED_SUBJECT_CERTIFICATE_REQUIRED',
          'Version-2 create execution must certify createdSubject=true.',
        );
      }

      if (
        !subjectMember ||
        !certifiedSubjectMatchesCreateProfileV2(
          subjectMember,
          input.createNewSubjectProfileV2!,
        )
      ) {
        return invalidCertificate(
          'E_V2_SUBJECT_PROFILE_MISMATCH',
          'Durable subject member does not exactly preserve the authorized version-2 profile payload.',
        );
      }
    }

    if (
      prepared.identityMode ===
        'REUSE_EXISTING' &&
      payload.createdSubject ===
        true
    ) {
      return invalidCertificate(
        'E_REUSE_CREATED_SUBJECT_INVALID',
        'Reuse-existing execution cannot certify that it created a new canonical subject.',
      );
    }

    if (
      !Object.prototype
        .hasOwnProperty.call(
          payload,
          'postState',
        ) ||
      payload.postState ===
        null ||
      typeof payload.postState !==
        'object'
    ) {
      return invalidCertificate(
        'E_POSTSTATE_REQUIRED',
        'Canonical Tree durable result is missing certified database poststate.',
      );
    }

    return Object.freeze({
      ok:
        true as const,

      status,

      operationId:
        input.operationId,

      subjectMemberId,

      auditId:
        input.operationId,

      createdSubject:
        typeof payload
          .createdSubject ===
          'boolean'
          ? payload.createdSubject
          : null,

      subjectMember,

      postState:
        payload.postState,

      rpcCount:
        1 as const,
    });
  } catch (error) {
    return rpcFailure(
      'E_RPC_UNEXPECTED_ERROR',
      error instanceof Error
        ? error.message
        : 'Unexpected canonical Tree atomic RPC failure.',
    );
  }
}

// 003G3WF4_PART1EB_END
